import { useEffect, useMemo, useRef, useState } from 'react'
import type * as Y from 'yjs'
import { useEditorState } from '@tiptap/react'
import type { Editor } from '@tiptap/react'
import { useLang, type Key } from '../../i18n'
import { uploadAndInsertImage } from '../../lib/image-upload'
import { api, type SvgResult } from '../../lib/api'
import { reportError } from '../../lib/errors'
import { beginAgentTask, useAgentBusy } from '../../lib/agent-busy'
import { useAgentAvailable } from '../../lib/agent-availability'
import { PromptModal } from '../PromptModal'
import { AnchorDialog } from '../AnchorDialog'
import { isLinkUrlAllowed } from './link-url'
import { ProseLinkDialog } from './ProseLinkDialog'
import { anchorAtSelection, removeAnchorIn, renameAnchorIn } from './anchor-mark'
import { anchorIdOfHref, defaultAnchorName, newAnchorId, type Anchor } from '../../lib/anchors'
import { useArtefactAnchors } from '../../lib/artefact-anchors'
import { tableAt, tableToGrid } from './table-to-grid'
import { ImageInsertModal } from './ImageInsertModal'
import { type HighlightColour } from './highlight-mark'
import { type TextColourName } from './text-colour-mark'
import { PROSE_PALETTE, RECENT_PROSE_COLOURS, colourAt, customColoursIn, proseSwatch, type ColourMark } from './prose-colours'
import { ColourButton, type ColourShown } from '../colour/ColourPicker'
import { useFormatPainter } from './useFormatPainter'
import { paintProse, proseMarksOf, wordAt, type ProseMarks } from './prose-format-painter'
import {
  IconAiImage,
  IconAnchor,
  IconBulletList,
  IconCode,
  IconFlowchart,
  IconHighlight,
  IconImage,
  IconLink,
  IconMore,
  IconNumberedList,
  IconPaintFormat,
  IconQuote,
  IconRedo,
  IconRule,
  IconStrike,
  IconTable,
  IconUndo,
  IconUnlink,
} from './ToolbarIcons'

interface EditorToolbarProps {
  /** The live tiptap editor, or null until the document is open/ready. */
  editor: Editor | null
  /** The active bid (for counting AI usage, e.g. SVG generation). */
  bid?: string | null
  /**
   * The caller is a VIEWER of this bid. The formatting buttons are already inert (tiptap is not
   * editable), but SVG generation is not a tiptap command — it calls the agent and inserts the
   * result — so it needs its own gate, or a viewer gets a button the backend refuses.
   */
  readOnly?: boolean
  /** The open part's room key, needed by "convert this table to a grid part" — it creates a
   *  SIBLING part in the same bid and artefact. Omitted = that action is unavailable. */
  room?: string | null
  /** Called with the new part's room once a table has been converted. */
  onConverted?: (room: string) => void
  /**
   * The caller is a BETA TESTER — gates "convert this table to a grid part", which CREATES a grid
   * part and would 403 for anyone else.
   *
   * A PROP, not `useBetaTester()`. Canvas renders this toolbar and is deliberately mountable
   * without a QueryClient (see CanvasProps#readOnly), so a hook here takes the whole editor down
   * in exactly the contexts that constraint exists to protect.
   */
  beta?: boolean
  /** The caller is an ADMIN of this bid — "convert this table to a grid part" is admin-only, on
   *  top of the beta gate. A prop for the same reason as `beta`. */
  admin?: boolean
  /** Publish a screen-reader announcement (the Canvas's live region) — the format painter's. */
  announce?: (text: string) => void
  /** The open part's live document and the collab server's URL (AB#350): with `room`, they let the
   *  Anchor button and the Link dialog's Anchor kind read every anchor of the artefact. Omitted =
   *  no anchors here (the Anchor button and kind are not offered). */
  ydoc?: Y.Doc | null
  wsUrl?: string
}

/** Reactive snapshot of which marks/nodes are active at the current selection. */
interface ToolbarState {
  bold: boolean
  italic: boolean
  strike: boolean
  /** The highlight colour at the selection: one value, none (null), or several ('mixed'). */
  highlight: string | null | 'mixed'
  /** The text colour at the selection, the same way. */
  textColour: string | null | 'mixed'
  link: boolean
  /** The caret / selection is inside an anchored range (AB#350). */
  anchor: boolean
  /** Nothing is selected (a caret). */
  empty: boolean
  bulletList: boolean
  orderedList: boolean
  blockquote: boolean
  codeBlock: boolean
  block: 'p' | 'h1' | 'h2' | 'h3'
  canUndo: boolean
  canRedo: boolean
  /** The caret is inside a table — the only state in which "convert to a grid part" applies. */
  table: boolean
  /** The editor takes edits (the format painter is off otherwise). */
  editable: boolean
}

/**
 * The Word-like formatting toolbar for the WYSIWYG editor. Every control is wired to a
 * tiptap command and reflects the live selection (active marks light up via `.on`). The
 * CRDT stays Markdown, so we only expose marks/nodes that round-trip through markdown
 * (no underline — it has no markdown form). Rendered by the Canvas above the paper, it
 * is inert (disabled) until a part is open.
 *
 * The bar is drawn in inline SVG (components/editor/ToolbarIcons.tsx), not emoji — see that
 * module for why. Two shape decisions come with it, both aimed at the same flat wall of ~17
 * buttons this used to be:
 *
 *  - **An overflow "…"** holds strikethrough, quote, code block and horizontal rule. They are
 *    real capabilities, just not ones a bid writer reaches for in the same breath as a heading
 *    or a list, and each carries a visible label in the menu — which is more discoverable than
 *    the bare `S`, `❝`, `</>` and `―` they replace.
 *  - **One image button.** "By URL" and "Upload" were two adjacent, near-identical glyphs for
 *    one intention; the route is now chosen inside the dialog (ImageInsertModal.tsx).
 */
export function EditorToolbar({ editor, bid, readOnly = false, room, onConverted, beta = false, admin = false, announce, ydoc, wsUrl }: EditorToolbarProps) {
  const { t } = useLang()

  // Re-render this toolbar on every selection/transaction so active states stay in sync.
  // A DESTROYED editor counts as "no editor": tiptap's useEditor destroys an instance on a timer
  // and the parent (Canvas) only drops its copy on a later state update, so a render can still
  // hand us a dead one — and `e.can()` on it throws (its commandManager is null), taking the whole
  // app down to the root error screen.
  const s = useEditorState({
    editor,
    selector: ({ editor: e }): ToolbarState | null => (!e || e.isDestroyed ? null : {
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      strike: e.isActive('strike'),
      highlight: colourAt(e, 'highlight'),
      textColour: colourAt(e, 'textColour'),
      link: e.isActive('link'),
      anchor: e.isActive('anchor'),
      empty: e.state.selection.empty,
      bulletList: e.isActive('bulletList'),
      orderedList: e.isActive('orderedList'),
      blockquote: e.isActive('blockquote'),
      codeBlock: e.isActive('codeBlock'),
      block: e.isActive('heading', { level: 1 })
        ? 'h1'
        : e.isActive('heading', { level: 2 })
          ? 'h2'
          : e.isActive('heading', { level: 3 })
            ? 'h3'
            : 'p',
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
      table: e.isActive('table'),
      editable: e.isEditable,
    }),
  })

  const off = !editor || editor.isDestroyed || !s
  /** A focused command chain, or null once the editor is gone (a click can land after destroy). */
  const focus = () => (editor && !editor.isDestroyed ? editor.chain().focus() : null)
  const [svgBusy, setSvgBusy] = useState(false)
  const [imgBusy, setImgBusy] = useState(false)
  const [modal, setModal] = useState<'image' | 'svg' | 'link' | 'anchor' | null>(null)
  // The href the Link dialog opens on — the one already under the caret, or '' for a new link.
  const [linkHref, setLinkHref] = useState('')
  const [converting, setConverting] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const moreBtnRef = useRef<HTMLButtonElement>(null)
  // The custom colours picked in this session, latest first; with the ones the part already uses,
  // they are the colour panel's "recent" row (the same list for text colour and highlight).
  const [picked, setPicked] = useState<string[]>([])
  const doc = editor && !editor.isDestroyed ? editor.state.doc : null
  const recentColours = useMemo(
    () => [...new Set([...picked, ...(doc ? customColoursIn(doc) : [])])].slice(0, RECENT_PROSE_COLOURS),
    [picked, doc],
  )
  /** Set (a colour) or remove (null) a colour mark on the selection: one transaction, so one undo
   *  step, and no other mark touched — the text colour never moves the highlight, nor the reverse. */
  function pickColour(mark: ColourMark, colour: string | null): void {
    const c = focus()
    if (!c) return
    if (colour === null) c.unsetMark(mark).run()
    else c.setMark(mark, { color: colour }).run()
    if (colour?.startsWith('#')) setPicked((p) => [colour, ...p.filter((x) => x !== colour)].slice(0, RECENT_PROSE_COLOURS))
  }

  // ── the format painter (AB#331, as the slides' AB#323) ──────────────────────────────────────
  // Armed once (the next selection made in the editor, or the word clicked, gets the style) or
  // sticky (every one, until Esc or the button again). Local UI state only; a paint is a plain
  // tiptap edit, so it reaches the CRDT and the history like any other human edit.
  const canPaint = !off && !readOnly && s.editable
  const painterBtnRef = useRef<HTMLButtonElement>(null)
  const say = (text: string): void => announce?.(text)
  const { painter, setPainter, painterRef, onClick: onPainterClick } = useFormatPainter<ProseMarks>({
    say,
    owns: (target) =>
      (!!editor && !editor.isDestroyed && !!editor.view.dom?.contains(target)) || !!painterBtnRef.current?.contains(target),
    sayOn: 'fmt_say_painter_on',
    saySticky: 'fmt_say_painter_sticky',
  })
  /** Ctrl+Shift+C's copied style, which Ctrl+Shift+V pastes (the slides' keyboard path). */
  const styleClip = useRef<ProseMarks | null>(null)
  const armed = painter !== null
  useEffect(() => setPainter(null), [editor, setPainter])
  useEffect(() => {
    if (!canPaint) setPainter(null)
  }, [canPaint, setPainter])
  /** Paint the selection, or the word at the caret (a click on a word). Whether it painted. */
  const paintHere = (src: ProseMarks): boolean => {
    if (!editor || editor.isDestroyed) return false
    const { empty, from } = editor.state.selection
    const word = empty ? wordAt(editor.state.doc, from) : null
    if (empty && !word) return false
    if (!paintProse(editor, src, word ?? undefined)) return false
    say(t('fp_painted'))
    return true
  }
  const paintHereRef = useRef(paintHere)
  paintHereRef.current = paintHere
  // Armed: a press in the editor, released anywhere, paints what it selected (read once the editor
  // has taken the press's final selection). Nothing selected and no word under it: still armed.
  useEffect(() => {
    const dom = armed && editor && !editor.isDestroyed ? editor.view.dom : null
    if (!dom) return
    let pressed = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const onDown = (e: MouseEvent): void => {
      pressed = e.button === 0 && dom.contains(e.target as Node)
    }
    const onUp = (): void => {
      if (!pressed) return
      pressed = false
      timer = setTimeout(() => {
        const p = painterRef.current
        if (p && paintHereRef.current(p.src) && !p.sticky) setPainter(null)
      }, 0)
    }
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('mouseup', onUp, true)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', onDown, true)
      document.removeEventListener('mouseup', onUp, true)
    }
  }, [armed, editor, painterRef, setPainter])
  // The brush cursor over the page while armed (on EditorContent's host: the editor's own element
  // has its class attribute managed by ProseMirror).
  useEffect(() => {
    const host = armed && editor && !editor.isDestroyed ? editor.view.dom?.parentElement : null
    if (!host) return
    host.classList.add('fmt-painting')
    return () => host.classList.remove('fmt-painting')
  }, [armed, editor])
  // Ctrl/Cmd+Shift+C copies the style at the caret / of the selection, Ctrl/Cmd+Shift+V pastes it
  // on the selection (or the word at the caret) — never the browser's plain-text paste.
  useEffect(() => {
    const dom = canPaint && editor ? editor.view.dom : null
    if (!dom) return
    const onKey = (e: KeyboardEvent): void => {
      const k = e.key.toLowerCase()
      if (!(e.ctrlKey || e.metaKey) || !e.shiftKey || e.altKey || (k !== 'c' && k !== 'v')) return
      e.preventDefault()
      if (k === 'c') styleClip.current = proseMarksOf(editor!)
      else if (styleClip.current) paintHereRef.current(styleClip.current)
    }
    dom.addEventListener('keydown', onKey, true)
    return () => dom.removeEventListener('keydown', onKey, true)
  }, [canPaint, editor])

  const agentBusy = useAgentBusy().busy
  // The "Generate an SVG with AI" button is part of the local-AI surface — hidden when OFF.
  const agentAvailable = useAgentAvailable()

  /**
   * A click anywhere outside the open "…" menu closes it — the whole app, not just this bar,
   * since a stray click in the document or the chat should dismiss it too. Same plain document
   * listener as the artefact explorer's row menu (LeftNav.tsx): it does not depend on this popup
   * happening to have the topmost z-index at the click point, and it does not swallow the click.
   */
  useEffect(() => {
    if (!moreOpen) return
    const closeIfOutside = (e: MouseEvent): void => {
      if ((e.target as Element | null)?.closest('.fmtmore')) return
      setMoreOpen(false)
    }
    document.addEventListener('mousedown', closeIfOutside)
    return () => document.removeEventListener('mousedown', closeIfOutside)
  }, [moreOpen])

  /** Run one of the overflow commands and close the menu behind it, ADO-style. */
  function fromMenu(run: () => void): void {
    setMoreOpen(false)
    run()
  }

  function insertImageUrl(url: string): void {
    if (url.trim()) focus()?.setImage({ src: url.trim() }).run()
  }

  /**
   * Upload the dropped/picked images and insert them at the caret, then close the dialog.
   *
   * Sequential, not `Promise.all`: each insert lands at the caret the previous one left behind,
   * so racing them would scramble the order of a multi-image drop.
   */
  async function insertImageFiles(files: File[]): Promise<void> {
    if (!editor || editor.isDestroyed) return
    setImgBusy(true)
    try {
      for (const file of files) await uploadAndInsertImage(editor, file)
      setModal(null)
    } catch {
      // api.request() already reported the failure; the dialog stays open so it can be retried.
    } finally {
      setImgBusy(false)
    }
  }

  /**
   * Lift the table the caret is in OUT of the prose and into its own GRID PART.
   *
   * ONE-WAY (see components/editor/table-to-grid.ts). The new part is created through the same
   * `POST /v1/artefacts/parts` everything else uses, with `kind: 'grid'` and the parsed table as
   * its seed; only once the server has confirmed it is the table removed from the document, so a
   * failed call never costs the member their table.
   */
  async function convertTableToGrid(): Promise<void> {
    if (!editor || editor.isDestroyed || !room) return
    const [bidId, artefactId] = room.split('/')
    if (!bidId || !artefactId) return
    const hit = tableAt(editor.state.doc, editor.state.selection.from)
    const grid = hit ? tableToGrid(hit.node) : null
    if (!hit || !grid) {
      reportError(t('grid_convert_fail'))
      return
    }
    setConverting(true)
    try {
      const created = await api.createPart(bidId, artefactId, t('grid_label'), 'grid', grid)
      // Confirmed: now — and only now — take the table out of the prose.
      if (!editor.isDestroyed) editor.chain().focus().deleteRange({ from: hit.from, to: hit.to }).run()
      onConverted?.(created.room)
    } catch {
      // request() already reported the failure, and the table stays where it was. Letting the
      // rejection escape added an "Unexpected error" dialog on top of that report.
    } finally {
      setConverting(false)
    }
  }

  /** Ask the local agent for an SVG from a prompt, then insert it as an ```svg block (renders). */
  async function generateSvg(prompt: string): Promise<void> {
    if (!editor || editor.isDestroyed || svgBusy || !prompt.trim()) return
    setSvgBusy(true)
    const release = beginAgentTask()
    try {
      const { jobId } = await api.agentSvg(prompt.trim(), undefined, bid ?? undefined)
      let job = await api.agentJob(jobId)
      while (job.status === 'running') {
        await new Promise((r) => setTimeout(r, 3000))
        job = await api.agentJob(jobId)
      }
      if (job.status === 'error') {
        reportError(job.error ?? 'The local agent failed to generate the SVG.')
        return
      }
      const { svg } = (job.result ?? {}) as SvgResult
      if (!svg || editor.isDestroyed) return
      editor
        .chain()
        .focus()
        .insertContent({ type: 'codeBlock', attrs: { language: 'svg' }, content: [{ type: 'text', text: svg }] })
        .run()
    } catch (e) {
      reportError(e instanceof Error ? e.message : 'The local agent failed to generate the SVG.')
    } finally {
      release()
      setSvgBusy(false)
    }
  }

  function setBlock(value: ToolbarState['block']): void {
    if (!editor || editor.isDestroyed) return
    if (value === 'p') focus()?.setParagraph().run()
    else focus()?.toggleHeading({ level: Number(value[1]) as 1 | 2 | 3 }).run()
  }

  /**
   * Open the Link dialog, pre-filled with the href already under the caret.
   *
   * This used to be a browser `prompt()`, which Electron's Chromium does not implement — it throws,
   * so the Desktop app could not create a single link (work item 45). It is an in-app dialog
   * (ProseLinkDialog: Web / E-mail / Anchor, AB#350).
   */
  function openLinkDialog(): void {
    if (!editor || editor.isDestroyed) return
    setLinkHref((editor.getAttributes('link').href as string | undefined) ?? '')
    setModal('link')
  }

  /** Apply the dialog's URL to the selection (or to the link the caret sits in). */
  function applyLink(url: string): void {
    if (!editor || editor.isDestroyed) return
    const href = url.trim()
    // Refused, not written: see components/editor/link-url.ts — an href reaches every member. (The
    // dialog already checked it; a link to an anchor, `#anchor-<id>`, has no scheme to check.)
    if (anchorIdOfHref(href) === null && !isLinkUrlAllowed(href)) {
      reportError(t('fmt_link_invalid'))
      return // dialog stays open, so the URL can be fixed rather than retyped
    }
    setModal(null)
    focus()?.extendMarkRange('link').setLink({ href }).run()
  }

  /** Drop the link mark but keep its text — the dialog's secondary action, and the bar's Unlink. */
  function removeLink(): void {
    if (!editor || editor.isDestroyed) return
    setModal(null)
    focus()?.extendMarkRange('link').unsetLink().run()
  }

  // ── anchors (AB#350) ─────────────────────────────────────────────────────────────────────────
  // Offered only where the editor knows its part (room + live doc): an anchor's name is unique in
  // the ARTEFACT, which takes reading the other parts too (lib/artefact-anchors.ts).
  const anchorCtx = !!(room && ydoc && wsUrl !== undefined)
  // Read while a window that needs them is up — the Anchor dialog (uniqueness) or the Link dialog.
  const listing = anchorCtx && (modal === 'anchor' || modal === 'link')
  const { anchors, failed } = useArtefactAnchors(listing ? { room: room!, ydoc: ydoc! } : null, wsUrl ?? '')
  /** The anchor the dialog edits (null: a new one), and a new one's default name. */
  const [anchorEdit, setAnchorEdit] = useState<{ initial: Anchor | null; defaultName: string }>({ initial: null, defaultName: '' })
  const canAnchor = anchorCtx && !off && !readOnly && s.editable && (s.anchor || !s.empty)

  function openAnchorDialog(): void {
    if (!editor || editor.isDestroyed) return
    const { from, to } = editor.state.selection
    setAnchorEdit({ initial: anchorAtSelection(editor), defaultName: defaultAnchorName(editor.state.doc.textBetween(from, to, ' ')) })
    setModal('anchor')
  }

  /** Create (on the selection) or rename (every range of it): one transaction, so one undo step. */
  function applyAnchor(name: string): void {
    setModal(null)
    const c = focus()
    if (!c) return
    const initial = anchorEdit.initial
    if (initial) {
      c.command(({ tr }) => renameAnchorIn(tr, initial.id, name)).run()
      say(t('an_say_renamed'))
    } else {
      c.setMark('anchor', { id: newAnchorId(), name }).run()
      say(t('an_say_added'))
    }
  }

  /** Remove the anchor everywhere it is, its text kept. */
  function removeAnchor(): void {
    setModal(null)
    const initial = anchorEdit.initial
    if (!initial) return
    focus()?.command(({ tr }) => removeAnchorIn(tr, initial.id)).run()
    say(t('an_say_removed'))
  }

  return (
    <>
      <div className="fmtgrp">
        <select
          className="fmtsel"
          title="Paragraph style"
          aria-label="Paragraph style"
          value={s?.block ?? 'p'}
          disabled={off}
          onChange={(e) => setBlock(e.target.value as ToolbarState['block'])}
        >
          <option value="p">{t('fmt_normal')}</option>
          <option value="h1">{t('fmt_h1')}</option>
          <option value="h2">{t('fmt_h2')}</option>
          <option value="h3">{t('fmt_h3')}</option>
        </select>
      </div>

      <div className="fmtgrp">
        <button
          className={`fmtbtn b${s?.bold ? ' on' : ''}`}
          title="Bold  (Ctrl+B  ·  **text**)"
          aria-label="Bold"
          aria-pressed={s?.bold}
          disabled={off}
          onClick={() => focus()?.toggleBold().run()}
        >
          B
        </button>
        <button
          className={`fmtbtn i${s?.italic ? ' on' : ''}`}
          title="Italic  (Ctrl+I  ·  *text*)"
          aria-label="Italic"
          aria-pressed={s?.italic}
          disabled={off}
          onClick={() => focus()?.toggleItalic().run()}
        >
          I
        </button>
        <ProseColourButton
          mark="textColour"
          at={s?.textColour ?? null}
          disabled={off}
          recent={recentColours}
          onPick={pickColour}
        />
        <ProseColourButton
          mark="highlight"
          at={s?.highlight ?? null}
          disabled={off}
          recent={recentColours}
          onPick={pickColour}
        />
        {/* The format painter: keeps the editor's focus and selection (no mousedown default). */}
        <button
          ref={painterBtnRef}
          className={`fmtbtn${painter ? ' on' : ''}`}
          title={`${t('fmt_copy_style_hint')} (Ctrl+Shift+C / Ctrl+Shift+V)`}
          aria-label={t('fp_copy_style')}
          aria-pressed={armed}
          disabled={!canPaint}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => onPainterClick(e, () => (editor && !editor.isDestroyed ? proseMarksOf(editor) : null))}
        >
          <IconPaintFormat />
        </button>
        <button
          className={`fmtbtn${s?.link ? ' on' : ''}`}
          title="Link  ([text](url))"
          aria-label="Link"
          aria-pressed={s?.link}
          disabled={off}
          onClick={openLinkDialog}
        >
          <IconLink />
        </button>
        {/* Its own button rather than a trip through the Link dialog: removing a link is one
            decision, already made by the time the caret is in it. Enabled only on a link — there
            is nothing to unlink otherwise. The dialog keeps its "Remove link" for the member who
            opened it to edit the href and changed their mind. */}
        <button
          className="fmtbtn"
          title={t('fmt_unlink')}
          aria-label={t('fmt_unlink')}
          disabled={off || !s?.link}
          onClick={removeLink}
        >
          <IconUnlink />
        </button>
        {/* Anchor (AB#350): on a selection, a new anchor; with the caret in one, rename / remove it. */}
        {anchorCtx && (
          <button
            className={`fmtbtn${s?.anchor ? ' on' : ''}`}
            title={t('an_anchor_hint')}
            aria-label={t('an_anchor')}
            aria-pressed={s?.anchor}
            disabled={!canAnchor}
            onClick={openAnchorDialog}
          >
            <IconAnchor />
          </button>
        )}
      </div>

      <div className="fmtgrp">
        <button
          className={`fmtbtn${s?.bulletList ? ' on' : ''}`}
          title="Bullet list  (- item)"
          aria-label="Bullet list"
          aria-pressed={s?.bulletList}
          disabled={off}
          onClick={() => focus()?.toggleBulletList().run()}
        >
          <IconBulletList />
        </button>
        <button
          className={`fmtbtn${s?.orderedList ? ' on' : ''}`}
          title="Numbered list  (1. item)"
          aria-label="Numbered list"
          aria-pressed={s?.orderedList}
          disabled={off}
          onClick={() => focus()?.toggleOrderedList().run()}
        >
          <IconNumberedList />
        </button>
      </div>

      <div className="fmtgrp">
        <button
          className="fmtbtn"
          title="Insert table"
          aria-label="Insert table"
          disabled={off}
          onClick={() => focus()?.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        >
          <IconTable />
        </button>
        {/* Only offered from INSIDE a table — a one-way lift out of the prose, for the table that
            has outgrown it. Deliberately named "grid part", never "table", so nobody expects the
            two to behave alike (a table illustrates the argument; a grid part IS the deliverable). */}
        {!off && !readOnly && s.table && room && beta && admin && (
          <button
            className="fmtbtn"
            title={t('grid_convert_title')}
            aria-label={t('grid_convert_title')}
            disabled={converting}
            onClick={() => void convertTableToGrid()}
          >
            {t('grid_convert')}
          </button>
        )}
        <button
          className="fmtbtn"
          title="Insert Mermaid diagram"
          aria-label="Insert Mermaid diagram"
          disabled={off}
          onClick={() =>
            focus()
              ?.insertContent({
                type: 'codeBlock',
                attrs: { language: 'mermaid' },
                content: [{ type: 'text', text: 'graph TD\n  A[Start] --> B[End]' }],
              })
              .run()
          }
        >
          <IconFlowchart />
        </button>
        <button
          className="fmtbtn"
          title={t('img_insert_title')}
          aria-label={t('img_insert_title')}
          disabled={off}
          onClick={() => setModal('image')}
        >
          <IconImage />
        </button>
        {agentAvailable && !readOnly && (
          <button
            className="fmtbtn"
            title={t('svg_gen')}
            aria-label={t('svg_gen')}
            disabled={off || agentBusy}
            onClick={() => setModal('svg')}
          >
            {svgBusy ? <span aria-hidden="true">…</span> : <IconAiImage />}
          </button>
        )}
      </div>

      {/* The overflow. A disclosure (button + aria-expanded + a labelled group), not a role="menu":
          this whole bar is already a role="toolbar", and its items stay plain buttons — which is
          what they are, and what keeps them reachable with Tab rather than only with the arrow-key
          convention a menu owes its users. */}
      <div className="fmtgrp">
        {/* Escape is caught on the WRAPPER, not on the popup: the focus is still on the "…"
            button the moment after it opens, which is outside the popup — a handler down there
            would only work once the member had already tabbed into the menu. */}
        <div
          className="fmtmore"
          onKeyDown={(e) => {
            if (!moreOpen || e.key !== 'Escape') return
            setMoreOpen(false)
            moreBtnRef.current?.focus()
          }}
        >
          <button
            ref={moreBtnRef}
            className={`fmtbtn${moreOpen ? ' on' : ''}`}
            title={t('fmt_more')}
            aria-label={t('fmt_more')}
            aria-expanded={moreOpen}
            disabled={off}
            onClick={() => setMoreOpen((open) => !open)}
          >
            <IconMore />
          </button>
          {moreOpen && (
            <div className="fmtpop" role="group" aria-label={t('fmt_more')}>
              <button
                className={s?.strike ? 'on' : undefined}
                title={`${t('fmt_strike')}  (~~text~~)`}
                aria-pressed={s?.strike}
                onClick={() => fromMenu(() => focus()?.toggleStrike().run())}
              >
                <IconStrike />
                {t('fmt_strike')}
              </button>
              <button
                className={s?.blockquote ? 'on' : undefined}
                title={`${t('fmt_quote')}  (> quote)`}
                aria-pressed={s?.blockquote}
                onClick={() => fromMenu(() => focus()?.toggleBlockquote().run())}
              >
                <IconQuote />
                {t('fmt_quote')}
              </button>
              <button
                className={s?.codeBlock ? 'on' : undefined}
                title={`${t('fmt_code')}  (\`\`\`)`}
                aria-pressed={s?.codeBlock}
                onClick={() => fromMenu(() => focus()?.toggleCodeBlock().run())}
              >
                <IconCode />
                {t('fmt_code')}
              </button>
              <button
                title={`${t('fmt_hr')}  (---)`}
                onClick={() => fromMenu(() => focus()?.setHorizontalRule().run())}
              >
                <IconRule />
                {t('fmt_hr')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="fmtgrp">
        <button
          className="fmtbtn"
          title="Undo  (Ctrl+Z)"
          aria-label="Undo"
          disabled={off || !s?.canUndo}
          onClick={() => focus()?.undo().run()}
        >
          <IconUndo />
        </button>
        <button
          className="fmtbtn"
          title="Redo  (Ctrl+Shift+Z)"
          aria-label="Redo"
          disabled={off || !s?.canRedo}
          onClick={() => focus()?.redo().run()}
        >
          <IconRedo />
        </button>
      </div>

      {modal === 'image' && (
        <ImageInsertModal
          busy={imgBusy}
          onInsertUrl={(v) => {
            setModal(null)
            insertImageUrl(v)
          }}
          onFiles={(files) => void insertImageFiles(files)}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'link' && (
        <ProseLinkDialog
          initialHref={linkHref}
          // Only offered on an existing link — there is nothing to remove otherwise.
          canRemove={!!s?.link}
          anchorKind={anchorCtx}
          anchors={anchors}
          failed={failed}
          currentRoom={room ?? undefined}
          onApply={applyLink}
          onRemove={removeLink}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'anchor' && (
        <AnchorDialog
          modal
          initial={anchorEdit.initial}
          defaultName={anchorEdit.defaultName}
          existing={anchors}
          onApply={applyAnchor}
          onRemove={removeAnchor}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'svg' && (
        <PromptModal
          title={t('svg_gen')}
          label={t('svg_gen_label')}
          placeholder={t('svg_gen_ph')}
          confirmLabel={t('svg_generate')}
          multiline
          onSubmit={(v) => {
            setModal(null)
            void generateSvg(v)
          }}
          onClose={() => setModal(null)}
        />
      )}
    </>
  )
}

/** Each palette colour's label key: the highlight's names, then the text colour's. */
const HIGHLIGHT_LABEL: Record<HighlightColour, Key> = {
  yellow: 'hl_yellow',
  green: 'hl_green',
  blue: 'hl_blue',
  pink: 'hl_pink',
  orange: 'hl_orange',
}
const TEXT_COLOUR_LABEL: Record<TextColourName, Key> = {
  blue: 'tc_blue',
  navy: 'tc_navy',
  green: 'tc_green',
  red: 'tc_red',
  purple: 'tc_purple',
  amber: 'tc_amber',
  grey: 'tc_grey',
}

/**
 * The Text colour or Highlight button (AB#287): the common colour panel the slides editor uses
 * (components/colour/ColourPicker.tsx), fed the prose palette — squares, the "Default colour" /
 * "No highlight" extra, the recent custom colours, the pipette and a # hex field. Its bar shows the
 * selection's colour, none or mixed. A pick applies in one transaction and never touches any other
 * mark; the extra removes this one.
 */
function ProseColourButton({
  mark,
  at,
  disabled,
  recent,
  onPick,
}: {
  mark: ColourMark
  at: string | null | 'mixed'
  disabled: boolean
  recent: string[]
  onPick: (mark: ColourMark, colour: string | null) => void
}) {
  const { t } = useLang()
  const highlight = mark === 'highlight'
  const label = highlight ? t('hl_highlight') : t('sl_text_colour')
  const nameOf = (v: string): string => {
    if (v.startsWith('#')) return v
    const key = highlight ? HIGHLIGHT_LABEL[v as HighlightColour] : TEXT_COLOUR_LABEL[v as TextColourName]
    return key ? t(key) : v
  }
  const shown: ColourShown = at === null ? 'none' : at === 'mixed' ? 'mixed' : { css: proseSwatch(mark, at), name: nameOf(at) }
  return (
    <ColourButton
      label={label}
      icon={highlight ? <IconHighlight /> : <span className="sl-glyph">A</span>}
      shown={shown}
      disabled={disabled}
      slots={PROSE_PALETTE[mark]}
      swatch={(v) => proseSwatch(mark, v)}
      slotName={nameOf}
      recent={recent}
      value={at === null || at === 'mixed' ? undefined : at}
      onPick={(c) => onPick(mark, c)}
      extras={[
        {
          key: 'none',
          label: highlight ? t('hl_none') : t('tc_default'),
          disabled: at === null,
          onPick: () => onPick(mark, null),
        },
      ]}
    />
  )
}
